'use client';

import type {FC, PropsWithChildren} from 'react';
import {startTransition, useEffect, useState} from 'react';
import clsx from 'clsx';
import {AnimatePresence, LayoutGroup, motion} from 'framer-motion';
import {usePathname} from 'next/navigation';

import {Header} from '@/components/header';

interface Props {
	menu: React.ReactNode;
	link365: React.ReactNode;
}

export const MenuAnimatorClient: FC<PropsWithChildren<Props>> = ({
	menu,
	link365,
	children,
}) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [searchValue, setSearchValue] = useState('');
	const [hasOpenedMenu, setHasOpenedMenu] = useState(false);
	const pathname = usePathname();

	useEffect(() => {
		if (menuOpen) {
			// FIXME: fix react hook error
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setHasOpenedMenu(true);
		}
	}, [menuOpen]);

	useEffect(() => {
		startTransition(() => {
			setMenuOpen(false);
			setSearchValue('');
		});
	}, [pathname]);

	return (
		<LayoutGroup>
			<AnimatePresence>
				{menuOpen && (
					<motion.div
						key="menu"
						variants={{
							initial: {bottom: '100vh'},
							animate: {
								bottom: 0,
								transition: {duration: 0.8},
							},
							exit: {
								bottom: '100vh',
								transition: {duration: 0.4},
							},
						}}
						initial="initial"
						animate="animate"
						exit="exit"
						className={clsx(
							'absolute',
							'flex',
							'flex-col',
							'h-dvh',
							'w-screen',
							'justify-center',
							'items-center',
							'bg-forestGreen-700',
							'gap-8',
							'z-50',
							'pb-8',
						)}
					>
						<Header
							menuOpen={true}
							setMenuOpen={setMenuOpen}
							hasOpenedMenu={hasOpenedMenu}
							searchValue={searchValue}
							setSearchValue={setSearchValue}
							menu={menu}
							link365={link365}
						/>
					</motion.div>
				)}
			</AnimatePresence>

			<AnimatePresence initial={false}>
				{!menuOpen && (
					<motion.div
						key="main"
						variants={{
							initial: {top: '100vh'},
							animate: {
								top: 0,
								transition: {duration: 0.4},
							},
							exit: {
								top: 0,
								transition: {duration: 0.8},
							},
						}}
						initial="initial"
						animate="animate"
						exit="exit"
						className={clsx(
							'relative',
							'h-full',
							'flex',
							'flex-col',
						)}
					>
						<Header
							menuOpen={false}
							setMenuOpen={setMenuOpen}
							hasOpenedMenu={hasOpenedMenu}
							searchValue={searchValue}
							setSearchValue={setSearchValue}
							link365={link365}
						/>
						{children}
					</motion.div>
				)}
			</AnimatePresence>
		</LayoutGroup>
	);
};
